import { each, toSeconds } from '@art-suite/art-core-ts';
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ApiCreateScanNote, ApiScanNote, getFlattenedScanNotes, getScanNotesQueryKey } from '@kaiizen/kai-pro-api';
import { QueryCache, useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';

import { useScanNoteSyncModel } from '../contexts/syncing/Models';
import { useAuth } from '../useAuth';
import { useKaiProApiClient } from '../useKaiProApiClient';
import { ScanOfflineDataMinTtlOnAccessMs } from './ScanStateLib';

/** The signed-in user and the scan are filled in by the mutator. */
export type ScanNoteMutators = {
  create: (record: Omit<ApiCreateScanNote, 'userId' | 'scanId'>) => Promise<ApiScanNote>;
};

type ScanNotesByScanPartId = Record<string, ApiScanNote[]>;

const getScanNotesByScanPartId = (scanNotes: ApiScanNote[] | undefined): ScanNotesByScanPartId => {
  const scanNotesByScanPartId: ScanNotesByScanPartId = {};
  if (!scanNotes) return scanNotesByScanPartId;
  for (const note of scanNotes) {
    if (!note.scanPartId) continue;
    (scanNotesByScanPartId[note.scanPartId] ??= []).push(note);
  }
  each(scanNotesByScanPartId, notes => notes.sort((a, b) => toSeconds(a.createdAt) - toSeconds(b.createdAt)));
  return scanNotesByScanPartId;
};

export const useScanNotesMutators = (scanId: string | undefined) => {
  const queryCache = useQueryCache() as QueryCache<ApiScanNote[]>;
  const { addScanNoteToSync } = useScanNoteSyncModel();
  const { userId } = useAuth();

  return useMemo(() => {
    return {
      create: async (scanNoteData: Omit<ApiCreateScanNote, 'userId' | 'scanId'>) => {
        if (!userId || !scanId) throw new Error('User ID and scan ID are required');
        if (!scanNoteData.scanPartId) throw new Error('Scan part ID is required');
        const queryKey = getScanNotesQueryKey(scanId);
        const record: ApiScanNote = { ...scanNoteData, id: uuidv4(), userId, scanId, createdAt: scanNoteData.createdAt || new Date() };
        await addScanNoteToSync(scanId, scanNoteData.scanPartId, record);
        const currentScanNotes = (await queryCache.cache.getEntry(queryKey))?.value || [];
        await queryCache.cache.set(queryKey, [...currentScanNotes, record]);
        return record;
      },
    };
  }, [userId, scanId, queryCache, addScanNoteToSync]);
};

export const useScanNotes = (scanId: string | undefined, scanPartId?: string, { prefetchOnly = false }: { prefetchOnly?: boolean } = {}) => {
  const kaiProApiClient = useKaiProApiClient();
  const queryKey = getScanNotesQueryKey(scanId);

  const query = useCachedQuery(
    queryKey,
    async () => scanId ? kaiProApiClient.getScanNotesByScanId(scanId) : [],
    {
      enabled: !!kaiProApiClient.token && !!scanId,
      prefetchOnly,
      minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs,
    },
  );

  const flattenedScanNotes = useMemo(() => query.data ? getFlattenedScanNotes(query.data) : [], [query.data]);

  const scanNotesByScanPartId = useMemo(() => getScanNotesByScanPartId(flattenedScanNotes), [flattenedScanNotes]);

  return {
    ...query,
    scanNotesByScanPartId,
    scanNotes: scanPartId ? scanNotesByScanPartId[scanPartId] || [] : query.data || [],
    scanNoteMutators: useScanNotesMutators(scanId),
  };
};
